import { useState, useCallback, useRef, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import getAgGridHeight from "../../../../utils/commonFunctions/getAgGridHeight";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { PaginationQuery, PaginationTypes } from "../../../../@types/pagination.types";
import initCapitalize from "../../../../utils/commonFunctions/initCapitalize";
import { CustomPagination } from "../../../shared";
import { UIContext, AuthContext } from "../../../../contexts";
import { Pagination } from "@material-ui/lab";
import { Button, IconButton, Tooltip, useMediaQuery } from "@material-ui/core";
import { api } from "../../../../utils/api";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";

import DeleteIcon from "@material-ui/icons/Delete";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "./members.css";
import { AssignedPaginatedMembers } from "../../../../@types/api/admin/user/user.types";
import getLimit from "../../../../utils/pagination/getLimit";
import ROLES from "../../../../constants/roles";
import encryption from "../../../../utils/commonFunctions/encryption";
import { dateTimeZoneConverter } from "../../../../utils/commonFunctions/date";
import { trackPromise } from "react-promise-tracker";
import { handleValueCellRenderer } from "../../../../utils/commonFunctions/cellRenderers";
import { CustomFloatingFilter } from "../../../shared";
import { CustomFilter } from "../../../shared";
import { UIFormatSSN } from "../../../../utils/commonFunctions/SSN";

const Members = () => {
	const { setDashboardHeader } = useContext(UIContext);
	const navigate = useNavigate();
	const [members, setMembers] = useState([]);
	const [gridHeight, setGridHeight] = useState("400px");
	const [membersFilterSchema, setMembersFilterSchema] = useState({
		first_name: { value: null, type: null },
		last_name: { value: null, type: null },
		date_of_birth: { value: null, type: null },
		ssn: { value: null, type: null },
		role: { value: null, type: null },
		group_name: { value: null, type: null },
		group_number: { value: null, type: null },
		location: { value: null, type: null }
	});
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	// eslint-disable-next-line no-unused-vars
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const agGridRef = useRef<any>(null);
	const [limit, setLimit] = useState<number | null>(null);
	const [pagination, setPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: undefined
	});
	const { currentPage, pageCount } = pagination;
	const { user } = useContext(AuthContext);
	const xl = useMediaQuery("(min-width:1600px)");
	const lg = useMediaQuery("(min-width:1200px)");
	const md = useMediaQuery("(min-width:800px)");
	const sm = useMediaQuery("(min-width:600px)");
	const xs = useMediaQuery("(min-width:400px)");

	// const _limit = useMemo(() => getLimit(xl, lg, md, sm, xs), [lg, md, sm, xl, xs]);

	const _limit = getLimit(xl, lg, md, sm, xs);

	// console.log("memoized-variable", _limit);

	const handleAgGridReady = (params: GridReadyEvent) => {
		// eslint-disable-next-line no-unused-vars
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	const handleViewMembersDetails = () => {
		const focusedCell = agGridRef.current.api.getFocusedCell();
		const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
		// console.log("Row", row);
		const {
			data: { role, group_number, SSN }
		} = row;

		if (role === ROLES.employee) navigate(`/employer/member?gn=${group_number}&&qs=${encryption(SSN) as string}`);
		else if (role === ROLES.dependent)
			navigate(`/employer/member/dependent?gn=${group_number}&&qs=${encryption(SSN) as string}`);
	};

	const handleDetailsChangeRender = () => {
		return (
			<Button
				className="ag-grid-cell-button"
				variant="outlined"
				onClick={handleViewMembersDetails}
				style={{ marginTop: "3px", marginBottom: "6px" }}
			>
				See Details
			</Button>
		);
	};

	const handlePageChange = useCallback(
		(event: React.ChangeEvent<unknown>, page: number) => {
			setPagination(
				Object.assign({}, pagination, {
					currentPage: page
				})
			);
		},
		[pagination]
	);

	const getMembers = useCallback(async () => {
		if (typeof limit === "number" && typeof pagination.currentPage === "number") {
			let paginationQuery: PaginationQuery = {
				limit: limit,
				page: pagination.currentPage
			};
			Object.entries(membersFilterSchema).map(([key, value]) => {
				if (value.value !== "" && value.value !== null) {
					paginationQuery = {
						...paginationQuery,
						[key]: value.value
					};
				}
			});
			//console.log(paginationQuery);
			const assignedPaginatedMembers: AssignedPaginatedMembers = await api.user.getAssignedPaginatedMembers(
				paginationQuery
			);
			setMembers(Object.assign([], assignedPaginatedMembers.members));
		}
		// const paginationQuery: PaginationQuery = {
		// 	limit: 30000,
		// 	page: 1
		// };
		// const assignedPaginatedMembers: AssignedPaginatedMembers = await trackPromise(
		// 	api.user.getAssignedPaginatedMembers(paginationQuery)
		// );
		// setMembers(Object.assign([], assignedPaginatedMembers.members));
	}, [limit, pagination.currentPage, membersFilterSchema]);

	const handleDateCellRenderer = ({ value }: { value: string }) => dateTimeZoneConverter(value);

	// const handleSSNCellRenderer = ({ value }: { value: string }) => {
	// 	const SSN = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
	// 	return (
	// 		<div>
	// 			{user &&
	// 			user?.role === ROLES.super_admin &&
	// 			((user?.first_name === "Chitrak" && user?.last_name === "Biswas") ||
	// 				(user?.first_name === "Anupam" && user?.last_name === "Biswas") ||
	// 				(user?.first_name === "Rahul" && user?.last_name === "Dutta") ||
	// 				(user?.first_name === "Suman" && user?.last_name === "Dutta"))
	// 				? SSN
	// 				: UIFormatSSN(SSN)}
	// 		</div>
	// 	);
	// };
	const HandleSSNCellRenderer = ({ value, data }: { value: string; data: any }) => {
		const [isVisible, setIsVisible] = useState(false);
		const { user } = useContext(AuthContext);
		const ssn = value ? value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5) : "";
		const SSN = UIFormatSSN(ssn);
		const formatSSN = (ssn: string, visible = false) => {
			if (!ssn) return "";
			if (visible) return ssn;
			const sanitizedSSN = SSN.endsWith("@") ? SSN.slice(0, -1) : SSN;
			const lastFour = sanitizedSSN.slice(-4);
			return `XXX-XX-${lastFour}`;
		};
		const handleToggleVisibility = async (e: React.MouseEvent) => {
			// console.log("Toggle visibility clicked");
			e.stopPropagation();

			if (!isVisible) {
				try {
					// console.log("Attempting to log SSN view...");
					const viewSSNAddLogPayload = {
						member_id: data._id,
						member_role: data.role
					};
					const response = await api.viewSSNLog.viewSSNAddLog(viewSSNAddLogPayload);
					// console.log("SSN view log response:", response);
				} catch (error) {
					console.error("Error logging SSN view:", error);
					return;
				}
			}
			setIsVisible(!isVisible);
		};

		const shouldShowFullSSN =
			user &&
			user?.role === ROLES.super_admin &&
			((user?.first_name === "Chitrak" && user?.last_name === "Biswas") ||
				(user?.first_name === "Anupam" && user?.last_name === "Biswas") ||
				(user?.first_name === "Rahul" && user?.last_name === "Dutta") ||
				(user?.first_name === "Suman" && user?.last_name === "Dutta"));

		const displayedSSN = isVisible ? SSN : formatSSN(SSN);

		return (
			<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
				<span>{displayedSSN}</span>
				<button
					// eslint-disable-next-line
					onClick={(e) => handleToggleVisibility(e)}
					// eslint-disable-next-line
					style={{
						background: "none",
						border: "none",
						cursor: "pointer",
						padding: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "20px",
						lineHeight: 1,
						color: "green"
					}}
					title={isVisible ? "Hide SSN" : "Show SSN"}
				>
					{isVisible ? <FaEye /> : <FaEyeSlash />}
				</button>
			</div>
		);
	};

	const handleUserRoleCellRenderer = ({ value }: { value: string }) => {
		const role = initCapitalize(value);
		return <div>{role}</div>;
	};
	const handleLocationNameCellRenderer = ({ value }: { value: any }) => {
		return <div>{value.location_number + " - " + value.location_name}</div>;
	};

	const handleRegisteredMemberCellRenderer = ({ value }: { value: boolean }) => {
		return <div>{value ? "Yes" : "No"}</div>;
	};

	const getMembersCount = useCallback(async () => {
		if (limit) {
			const memberCount = await api.user.getMemberCount();
			const pageCount = Math.ceil(memberCount / limit);
			// console.log("pageCount", pageCount);
			setPagination(Object.assign({}, pagination, { pageCount }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [limit]);

	useEffect(() => {
		setLimit(_limit);
		getMembersCount();
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.all_group_members);
	}, [_limit, getMembersCount, setDashboardHeader]);

	useEffect(() => {
		if (limit && pagination.pageCount) {
			getMembers();
		}
	}, [pagination, limit, getMembers]);

	// useEffect(() => {
	// 	getMembers();
	// 	setDashboardHeader(ADMIN_DASHBOARD_HEADER.all_group_members);
	// }, [getMembers, setDashboardHeader]);

	useEffect(() => {
		const _gridHeight = getAgGridHeight(members.length);
		setGridHeight(_gridHeight);
	}, [members.length]);

	// console.log("limit", limit);
	// console.log("pagination", pagination);
	// console.log("Members----->", members);

	return (
		<div className="employee-dependent-container">
			<div className="employee-dependent-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={members}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						animateRows
						paginationPageSize={6}
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						enableCellChangeFlash={true}
						defaultColGroupDef={{ marryChildren: true }}
						frameworkComponents={{
							handleDetailsChangeRender: handleDetailsChangeRender,
							handleLocationNameCellRenderer: handleLocationNameCellRenderer,
							schema: membersFilterSchema,
							handleDateCellRenderer: handleDateCellRenderer,
							handleSSNCellRenderer: HandleSSNCellRenderer,
							handleUserRoleCellRenderer: handleUserRoleCellRenderer,
							handleRegisteredMemberCellRenderer: handleRegisteredMemberCellRenderer,
							handleValueCellRenderer: handleValueCellRenderer,
							setPayloadSchema: setMembersFilterSchema,
							setParentSchema: setMembers,
							customFloatingFilter: CustomFloatingFilter,
							customFilter: CustomFilter
						}}
						rowSelection={"single"}
					>
						{/* {user &&
						user.role === ROLES.super_admin &&
						((user.first_name === "Chitrak" && user.last_name === "Biswas") ||
							(user.first_name === "Anupam" && user.last_name === "Biswas") ||
							(user.first_name === "Rahul" && user.last_name === "Dutta") ||
							(user.first_name === "Suman" && user.last_name === "Dutta")) ? (
							<AgGridColumn
								field="group_number"
								headerName="Group Number"
								suppressSizeToFit
							></AgGridColumn>
						) : null} */}
						<AgGridColumn
							field="member_id"
							headerName="Member Id"
							suppressSizeToFit
							// checkboxSelection={true}
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleValueCellRenderer"
							// floatingFilterComponentParams={{
							// 	suppressFilterButton: true
							// }}
							floatingFilterComponent="customFloatingFilter"
							filter="customFilter"
						></AgGridColumn>
						<AgGridColumn
							field="first_name"
							headerName="First Name"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							floatingFilterComponent="customFloatingFilter"
							filter="customFilter"
							onCellClicked={handleViewMembersDetails}
							cellStyle={() => {
								return { cursor: "pointer" };
							}}
						></AgGridColumn>
						<AgGridColumn
							field="last_name"
							headerName="Last Name"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							floatingFilterComponent="customFloatingFilter"
							filter="customFilter"
							onCellClicked={handleViewMembersDetails}
							cellStyle={() => {
								return { cursor: "pointer" };
							}}
						></AgGridColumn>
						<AgGridColumn
							field="date_of_birth"
							headerName="Date Of Birth"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleDateCellRenderer"
							filter={false}
						></AgGridColumn>
						<AgGridColumn
							field="SSN"
							headerName="SSN"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleSSNCellRenderer"
							floatingFilterComponent="customFloatingFilter"
							filter="customFilter"
							onCellClicked={handleViewMembersDetails}
							cellStyle={() => {
								return { cursor: "pointer" };
							}}
						></AgGridColumn>
						<AgGridColumn
							field="is_registered"
							headerName="Registered Member"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleRegisteredMemberCellRenderer"
							filter={false}
						></AgGridColumn>
						<AgGridColumn
							field="role"
							headerName="Role"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleUserRoleCellRenderer"
							floatingFilterComponent="customFloatingFilter"
							filter="customFilter"
						></AgGridColumn>
						<AgGridColumn
							field="group_number"
							headerName="Group Number"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							// floatingFilterComponent="customFloatingFilter"
							// filter="customFilter"
							filter={false}
						></AgGridColumn>
						<AgGridColumn
							field="group.name"
							headerName="Group Name"
							suppressSizeToFit
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							// floatingFilterComponent="customFloatingFilter"
							// filter="customFilter"
							filter={false}
						></AgGridColumn>
						<AgGridColumn
							field="location"
							headerName="Location"
							suppressSizeToFit
							cellRenderer="handleLocationNameCellRenderer"
							//floatingFilterComponentParams={{ suppressFilterButton: true }}
							// floatingFilterComponent="customFloatingFilter"
							// filter="customFilter"
							filter={false}
						></AgGridColumn>
						<AgGridColumn
							field="action"
							headerName="Action"
							cellRenderer="handleDetailsChangeRender"
							filter={false}
							suppressSizeToFit={true}
						></AgGridColumn>
					</AgGridReact>
				</div>
			</div>
			<div className="custom-pagination" id="custom-pagination">
				<CustomPagination pageCount={pageCount} currentPage={currentPage} handlePageChange={handlePageChange} />
			</div>
		</div>
	);
};

export default Members;
